/**
 * Period keys for the usage views.
 *
 * Computed on the client rather than fetched: which periods exist is a pure
 * function of today, and an endpoint to enumerate them would be a query per
 * page load for something we can derive. The keys must match what the rollup
 * job writes — UTC days, UTC-Monday weeks, UTC months.
 *
 * See docs/FeatureDesigns/user-activity-analytics.md.
 */

import type { RollupPeriod } from './types';

/** How many closed periods the picker offers, and how far a trend looks back. */
export const PERIOD_LOOKBACK: Record<RollupPeriod, number> = { day: 30, week: 12, month: 12 };

export function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** UTC Monday of the week containing `d` — the rollup's week key. */
export function mondayOf(d: Date): Date {
  const out = new Date(d);
  // getUTCDay() is 0 on Sunday, so shift it to 7 before subtracting.
  out.setUTCDate(out.getUTCDate() - ((out.getUTCDay() || 7) - 1));
  return out;
}

/** The closed periods a user can pick, newest first. The current period is
 *  excluded — it has no rollup until it closes. */
export function periodOptions(period: RollupPeriod, now: Date): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];

  for (let i = 1; i <= PERIOD_LOOKBACK[period]; i++) {
    if (period === 'day') {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - i);
      out.push({
        value: isoDay(d),
        label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      });
    } else if (period === 'week') {
      const d = mondayOf(now);
      d.setUTCDate(d.getUTCDate() - 7 * i);
      out.push({
        value: isoDay(d),
        label: `Week of ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}`,
      });
    } else {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      out.push({
        value: d.toISOString().slice(0, 7),
        label: d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
      });
    }
  }
  return out;
}

/** Lower bound for a trend query at this grain. */
export function trendFrom(period: RollupPeriod, now: Date): string {
  const d = new Date(now);
  if (period === 'day') {
    d.setUTCDate(d.getUTCDate() - PERIOD_LOOKBACK.day);
    return isoDay(d);
  }
  if (period === 'week') {
    // Anchored on the Monday, exactly like the keys it will be compared
    // against — measuring back from mid-week lands after the oldest week the
    // picker offers, quietly clipping the start of the series.
    const monday = mondayOf(d);
    monday.setUTCDate(monday.getUTCDate() - 7 * PERIOD_LOOKBACK.week);
    return isoDay(monday);
  }
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - PERIOD_LOOKBACK.month, 1))
    .toISOString().slice(0, 7);
}
